import { parseDateOrInstant } from "@mediatechgroup/calendar-date";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectSettings } from "../store/slices/settingsSlice";

/**
 * Tenant-configurable currency / locale formatters (L2/L3/L8).
 *
 * Reads `settings.currency_code`, `currency_decimal_places`, and
 * `locale` from the Redux settings slice. Falls back to USD/2/en-US
 * when settings haven't loaded yet OR the columns aren't populated —
 * matches GoY's experience and the old hardcoded formatters.
 *
 * Use cases:
 *   - Canadian club        → 'CAD' / 'en-CA'
 *   - UK studio            → 'GBP' / 'en-GB'
 *   - Spanish-speaking gym → 'USD' / 'es-MX'
 *
 * Returns memoized `Intl` formatters and a few wrapper helpers so
 * callers don't have to instantiate `Intl.NumberFormat` per-render.
 *
 * Replacement targets:
 *   - `${value.toFixed(2)}`  →  `formatCurrency(value)`
 *   - `${date.toLocaleDateString()}`  →  `formatDate(date)`
 *   - hardcoded weekday lookup → `formatWeekday(dayIndex)`
 */
export interface TenantFormat {
  currencyCode: string;
  decimalPlaces: number;
  locale: string;

  /** Format a numeric amount as currency, e.g. 12.5 → "$12.50" (USD/en-US). */
  formatCurrency: (amount: number | string | null | undefined) => string;
  /** Format a numeric value with grouping + decimals, no currency symbol. */
  formatNumber: (value: number | string | null | undefined) => string;
  /** Format a Date / ISO string in tenant locale, short style by default. */
  formatDate: (
    value: Date | string | null | undefined,
    style?: "short" | "medium" | "long" | "full"
  ) => string;
  /** Format a Date / ISO string with date + time. */
  formatDateTime: (value: Date | string | null | undefined) => string;
  /** 0=Sunday … 6=Saturday — full weekday name in tenant locale. */
  formatWeekday: (dayIndex: number) => string;
  /** Same as formatWeekday but the short variant ("Mon" vs "Monday"). */
  formatWeekdayShort: (dayIndex: number) => string;
}

const DEFAULT_CURRENCY = "USD";
const DEFAULT_DECIMALS = 2;
const DEFAULT_LOCALE = "en-US";

const safeNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
};

// A bare "YYYY-MM-DD" is a calendar day (local midnight); anything with a
// time is an instant. See @mediatechgroup/calendar-date's date-only helpers.
const safeDate = (value: Date | string | null | undefined): Date | null =>
  parseDateOrInstant(value);

export function useTenantFormat(): TenantFormat {
  const settings: any = useSelector(selectSettings);

  const currencyCode =
    (settings?.currency_code || "").toString().trim().toUpperCase() ||
    DEFAULT_CURRENCY;
  const decimalPlaces = (() => {
    const raw = settings?.currency_decimal_places;
    if (raw === null || raw === undefined || raw === "") return DEFAULT_DECIMALS;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || n > 4) return DEFAULT_DECIMALS;
    return Math.trunc(n);
  })();
  const locale =
    (settings?.locale || "").toString().trim() || DEFAULT_LOCALE;

  return useMemo<TenantFormat>(() => {
    // Construct Intl formatters once per settings change. Wrapping in
    // try/catch shields against a tenant typing an invalid locale —
    // we silently fall back to en-US rather than throwing in a render.
    let currencyFmt: Intl.NumberFormat;
    let numberFmt: Intl.NumberFormat;
    let weekdayLong: Intl.DateTimeFormat;
    let weekdayShort: Intl.DateTimeFormat;
    let dateShort: Intl.DateTimeFormat;
    let dateTimeFmt: Intl.DateTimeFormat;
    try {
      currencyFmt = new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
      numberFmt = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
      weekdayLong = new Intl.DateTimeFormat(locale, { weekday: "long" });
      weekdayShort = new Intl.DateTimeFormat(locale, { weekday: "short" });
      dateShort = new Intl.DateTimeFormat(locale, { dateStyle: "short" });
      dateTimeFmt = new Intl.DateTimeFormat(locale, {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch {
      // Invalid locale or currency — fall back to en-US/USD silently.
      currencyFmt = new Intl.NumberFormat(DEFAULT_LOCALE, {
        style: "currency",
        currency: DEFAULT_CURRENCY,
        minimumFractionDigits: DEFAULT_DECIMALS,
        maximumFractionDigits: DEFAULT_DECIMALS,
      });
      numberFmt = new Intl.NumberFormat(DEFAULT_LOCALE, {
        minimumFractionDigits: DEFAULT_DECIMALS,
        maximumFractionDigits: DEFAULT_DECIMALS,
      });
      weekdayLong = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: "long" });
      weekdayShort = new Intl.DateTimeFormat(DEFAULT_LOCALE, { weekday: "short" });
      dateShort = new Intl.DateTimeFormat(DEFAULT_LOCALE, { dateStyle: "short" });
      dateTimeFmt = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
        dateStyle: "short",
        timeStyle: "short",
      });
    }

    return {
      currencyCode,
      decimalPlaces,
      locale,
      formatCurrency: (amount) => currencyFmt.format(safeNumber(amount)),
      formatNumber: (value) => numberFmt.format(safeNumber(value)),
      formatDate: (value, style = "short") => {
        const d = safeDate(value);
        if (!d) return "";
        if (style === "short") return dateShort.format(d);
        try {
          return new Intl.DateTimeFormat(locale, { dateStyle: style }).format(d);
        } catch {
          return dateShort.format(d);
        }
      },
      formatDateTime: (value) => {
        const d = safeDate(value);
        return d ? dateTimeFmt.format(d) : "";
      },
      formatWeekday: (dayIndex) => {
        // Sunday=0 anchored against a known Sunday in 2024.
        const base = new Date(Date.UTC(2024, 0, 7 + (dayIndex % 7))); // 2024-01-07 was Sunday
        return weekdayLong.format(base);
      },
      formatWeekdayShort: (dayIndex) => {
        const base = new Date(Date.UTC(2024, 0, 7 + (dayIndex % 7)));
        return weekdayShort.format(base);
      },
    };
  }, [currencyCode, decimalPlaces, locale]);
}
